import React, { useState } from "react";
import type { ReactNode } from "react";
import { FieldArray } from "react-final-form-arrays";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import type { DroppableProvided, DropResult } from "@hello-pangea/dnd";
import type { FieldArrayRenderProps } from "react-final-form-arrays";
import type { ArrayFieldDefinition, FieldDefinition } from "./types";
import { ArrayFieldItem } from "./ArrayFieldItem";
import { Stack } from "@mui/material";
import { useCopyPaste } from "./hooks";
import { ErrorMessage } from "../ErrorMessage";

interface ArrayFieldProps {
  fieldName: string;
  field: ArrayFieldDefinition;
  values: Record<string, unknown>;
  allValues: Record<string, unknown>;
  spacing?: number;
  editData?: Record<string, unknown>;
  getField: (
    fieldName: string,
    field: FieldDefinition,
    values: Record<string, unknown>,
    allValues: Record<string, unknown>
  ) => ReactNode;
}

const makeOnDragEndFunction =
  (fields: FieldArrayRenderProps<any, HTMLElement>["fields"]) =>
  (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    fields.move(result.source.index, result.destination.index);
  };

export const ArrayField: React.FC<ArrayFieldProps> = ({
  fieldName,
  field,
  values,
  allValues,
  spacing = 2,
  editData,
  getField,
}) => {
  const { t } = useTranslation();

  const getListStyle = (): React.CSSProperties => ({
    background: "transparent",
    transition: "background 0.3s ease",
    padding: 0,
    width: "100%",
  });

  const {
    label,
    xs,
    sm,
    md,
    lg,
    xl,
    subschema,
    addText,
    removeText,
    restoreText,
    softDelete,
    getNewItem,
    disableAdd = false,
    disableRemove = false,
    disableReorder = false,
    hideAddButton = false,
    hideRemoveButton = false,
    collapsible = false,
    defaultCollapsed = false,
    accordionMode = false,
    showCopyPaste = false,
    icon,
    getTitle,
    titleAction,
    pasteConfig,
    itemComponent,
    ...arrayProps
  } = field;

  // Состояние для управления accordion mode
  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    accordionMode && !defaultCollapsed ? 0 : null
  );

  const resolvedAddText =
    addText ?? t("shared.form.add", { defaultValue: "Добавить" });
  const resolvedRemoveText =
    removeText ?? t("shared.form.remove", { defaultValue: "Удалить" });
  const resolvedRestoreText =
    restoreText ?? t("shared.form.restore", { defaultValue: "Восстановить" });

  const { handleCopy, handlePaste } = useCopyPaste(
    fieldName, // Автоматически используем fieldName как parentArrayField
    pasteConfig || {}
  );

  // Состояние для отслеживания новых элементов в обычном режиме
  const [newlyAddedIndexes, setNewlyAddedIndexes] = useState<Set<number>>(
    new Set()
  );

  const onClickAdd = (
    fields: FieldArrayRenderProps<any, HTMLElement>["fields"]
  ) => {
    let newItem: unknown = subschema ? {} : "";
    if (getNewItem) {
      newItem = getNewItem({ values, allValues });
    }

    // Вычисляем индекс нового элемента ДО добавления
    const newIndex = fields.value ? fields.value.length : 0;

    fields.push(newItem);

    if (accordionMode) {
      setExpandedIndex(newIndex);
    } else {
      // Для обычного режима добавляем в список новых элементов
      setNewlyAddedIndexes((prev) => new Set([...prev, newIndex]));
    }
  };

  const getItemTitle = (item: any, index: number) => {
    if (getTitle) {
      return getTitle({ item, index, values, allValues, editData });
    }
    return t("shared.form.arrayItemTitle", {
      defaultValue: "Элемент #{{number}}",
      number: index + 1,
    });
  };
  return (
    <Grid
      key={fieldName}
      size={{
        xs: xs,
        sm: sm,
        md: md,
        lg: lg,
        xl: xl,
      }}
    >
      <FieldArray name={fieldName}>
        {({ fields, meta }: FieldArrayRenderProps<any, HTMLElement>) => (
          <DragDropContext
            onDragEnd={
              disableReorder ? () => undefined : makeOnDragEndFunction(fields)
            }
          >
            <div>
              {meta.touched && typeof meta.error == "string" && (
                <ErrorMessage error={{ message: meta.error }} />
              )}

              {label && (
                <Stack
                  mb={2}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {icon && (
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ color: "text.secondary" }}
                      >
                        {icon}
                      </Box>
                    )}
                    <Typography variant="h6">
                      {typeof label === "function"
                        ? label({ values, allValues })
                        : label}
                    </Typography>
                  </Box>
                  {titleAction}
                </Stack>
              )}

              <Droppable
                droppableId="droppable"
                isDropDisabled={disableReorder}
                isCombineEnabled={false}
                ignoreContainerClipping={false}
                direction="vertical"
                type="DEFAULT"
              >
                {(provided: DroppableProvided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={getListStyle()}
                  >
                    {fields.map((name: string, index: number) => {
                      const item = fields.value ? fields.value[index] : {};
                      const itemTitle = getItemTitle(item, index);

                      // Используем кастомный компонент, если он задан
                      if (itemComponent) {
                        const ItemComponent = itemComponent;
                        return (
                          <ItemComponent
                            key={name}
                            name={name}
                            index={index}
                            item={item}
                            fields={fields}
                            fieldName={fieldName}
                            subschema={subschema}
                            spacing={spacing}
                            allValues={allValues}
                            getField={getField}
                            removeText={resolvedRemoveText}
                            restoreText={resolvedRestoreText}
                            softDelete={softDelete}
                            disableRemove={disableRemove}
                            disableReorder={disableReorder}
                            hideRemoveButton={hideRemoveButton}
                            touched={meta.touched}
                            error={
                              Array.isArray(meta.error)
                                ? meta.error[index]
                                : undefined
                            }
                            isLast={index + 1 === fields.length}
                          />
                        );
                      }

                      return (
                        <ArrayFieldItem
                          key={name}
                          name={name}
                          index={index}
                          item={item}
                          fields={fields}
                          fieldName={fieldName}
                          subschema={subschema}
                          spacing={spacing}
                          allValues={allValues}
                          getField={getField}
                          arrayProps={arrayProps}
                          removeText={resolvedRemoveText}
                          restoreText={resolvedRestoreText}
                          softDelete={softDelete}
                          disableRemove={disableRemove}
                          disableReorder={disableReorder}
                          hideRemoveButton={hideRemoveButton}
                          collapsible={collapsible}
                          defaultCollapsed={defaultCollapsed}
                          itemTitle={itemTitle}
                          onCopy={showCopyPaste ? handleCopy : undefined}
                          onPaste={showCopyPaste ? handlePaste : undefined}
                          showCopyPaste={showCopyPaste}
                          expandedIndex={
                            accordionMode ? expandedIndex : undefined
                          }
                          setExpandedIndex={
                            accordionMode ? setExpandedIndex : undefined
                          }
                          isNewlyAdded={
                            !accordionMode && newlyAddedIndexes.has(index)
                          }
                          onExpandChange={
                            !accordionMode
                              ? () => {
                                  // Убираем из списка новых элементов при первом взаимодействии
                                  setNewlyAddedIndexes((prev) => {
                                    const newSet = new Set(prev);
                                    newSet.delete(index);
                                    return newSet;
                                  });
                                }
                              : undefined
                          }
                          touched={meta.touched}
                          error={
                            Array.isArray(meta.error)
                              ? meta.error[index]
                              : undefined
                          }
                        />
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              {!hideAddButton && (
                <div style={{ marginTop: 16 }}>
                  <Button
                    onClick={() => onClickAdd(fields)}
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    startIcon={<AddIcon />}
                    disabled={disableAdd}
                  >
                    {resolvedAddText}
                  </Button>
                </div>
              )}
            </div>
          </DragDropContext>
        )}
      </FieldArray>
    </Grid>
  );
};
